import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { getTranslate, getActiveLanguage } from 'react-localize-redux';
import { serviceLocator } from 'infrastructure/ServiceLocator';
import { push } from 'react-router-redux';
import routes from 'library/Routes';
import LayoutHelper from 'infrastructure/LayoutHelper';
import UserHelper from 'infrastructure/UserHelper';
import BookingClassInfoForm from './BookingClassInfoForm';
import {
  Link,
  Paper,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Grid,
  Typography,
  Avatar,
  Collapse,
  SnackbarContent,
  Snackbar,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import InfoIcon from '@material-ui/icons/Info';
import queryString from 'query-string';
import moment from 'moment-timezone';
import { Course, Domain, Studio, Teacher, User } from '../../types';

interface ClassInfo {
  courseId: string;
  className: string;
  classDateTime: string;
  teacherId?: string[];
  instructorName?: string;
  roomName?: string[];
  attendancePrice?: number;
  note?: string;
  limit?: number;
  canceled?: boolean;
}

interface ClientAttendance {
  _id: string;
  groupId: string;
  ownerId: string;
  studioClientId: string;
}

// Component interfaces
interface BookingClassInfoViewProps {
  classes: {
    root: string;
    paper: string;
    avatar: string;
    [key: string]: string;
  };
  translate: (key: string, options?: any) => string;
  currentLanguage: string;
  user: User;
  studio: Studio;
  domain: Domain;
  orderId?: string;
  match: {
    params: {
      domainName: string;
      groupId: string;
    };
  };
  location: {
    search: string;
  };
  registerToClass: (studio: Studio, classId: string) => void;
  toClassSignUp: (link: string) => void;
  clientAttendance: (studio: Studio, groupId: string, attendanceId: string) => void;
}

interface BookingClassInfoViewState {
  clientToAdd: User;
  studio?: Studio;
  owner?: User;
  isGuest: boolean;
  canSubmit: boolean;
  classInfo?: ClassInfo;
  course?: Course;
  groupId: string;
  attending: number;
  teachersMap: { [key: string]: Teacher };
  teachers: Teacher[];
  isNewClient: boolean;
  showPricingDetails?: boolean;
  showErrorSnackbar?: boolean;
  loadingOnlinePayment?: boolean;
  clientAttendance?: ClientAttendance;
  spotsLeft?: number;
}

class BookingClassInfoView extends React.Component<BookingClassInfoViewProps, BookingClassInfoViewState> {
  constructor(props: BookingClassInfoViewProps) {
    super(props);
    let clientToAdd: User;
    let isGuest = true;
    if (UserHelper.isLoggedIn(this.props.user)) {
      clientToAdd = this.props.user;
      isGuest = false;
    } else {
      clientToAdd = {
        phone: undefined,
        email: undefined,
        name: undefined,
        domain: props.domain.id,
      };
      isGuest = true;
    }
    const pageState = (window as any).__PAGE_STATE__;
    this.state = {
      clientToAdd: clientToAdd,
      studio: pageState && pageState.studio,
      owner: pageState && pageState.studioOwner,
      isGuest: isGuest,
      canSubmit: !isGuest,
      classInfo: pageState && pageState.classInfo,
      course: pageState && pageState.course,
      groupId: props.match.params.groupId,
      attending: 0,
      teachersMap: {},
      teachers: [],
      isNewClient: true,
    };
  }

  componentWillMount() {
    if (!this.state.studio) {
      let api = serviceLocator.studioApi;
      let domainName = this.props.match.params.domainName;

      return api.getStudioByDomainName(domainName).then(this.onSuccess, LayoutHelper.onErr);
    } else {
      this.loadOrder(this.state.studio);
      this.loadClassInfo(this.state.studio);
    }
  }

  onSuccess = (studio: Studio) => {
    this.setState({ studio: studio });
    this.loadOwner(studio.owner_id);
    this.loadClassInfo(studio);
    this.loadOrder(studio);
  };

  loadOrder = async (studio: Studio) => {
    if (this.props.orderId) {
      let api = serviceLocator.paymentApi;
      this.setState({ loadingOnlinePayment: true });
      let timerId = setInterval(async () => {
        const onlinePayment = await api.getOnlinePaymentById(studio.owner_id, this.props.orderId);

        if (onlinePayment) {
          if (onlinePayment.attendanceId) {
            clearInterval(timerId);
            this.props.clientAttendance(studio, onlinePayment.groupId, onlinePayment.attendanceId);
            return;
          }
        }
      }, 3000);
      setTimeout(() => {
        clearInterval(timerId);
        this.setState({ loadingOnlinePayment: false });
      }, 12000);
    }
  };

  loadClassInfo = (studio: Studio) => {
    let api = serviceLocator.registrationApi;
    api.getClassInfo(studio.id, this.state.groupId).then((data: any) => {
      let classInfo = data.classInfo;
      let attending = data.attending;
      let clientAttendance = data.clientAttendance;
      let spotsLeft;
      if (classInfo.limit) {
        spotsLeft = classInfo.limit - attending;
      }
      const schedule = studio.schedule;
      const course = schedule.repeatableEventsMap[classInfo.courseId];
      this.setState({
        classInfo,
        attending,
        clientAttendance,
        spotsLeft,
        course,
      });
    }, LayoutHelper.onErr);
    this.loadTeachers(studio.owner_id);
  };

  loadOwner = (ownerId: string) => {
    let api = serviceLocator.userApi;
    api.getOwnerInfo(ownerId).then((owner: User) => {
      this.setState({ owner: owner });
    }, LayoutHelper.onErr);
  };

  loadTeachers = (ownerId: string) => {
    let api = serviceLocator.teacherApi;

    api.getPublicTeachers(ownerId).then((data: { teachers: Teacher[] }) => {
      let teachers = data.teachers;
      let teachersMap: { [key: string]: Teacher } = {};
      for (let i = 0; i < teachers.length; i++) {
        let teacher = teachers[i];
        teachersMap[teacher._id] = teacher;
      }
      this.setState({ teachers: teachers, teachersMap: teachersMap });
    }, LayoutHelper.onErr);
  };

  render() {
    let { classes } = this.props;

    return (
      <div className={classes.root}>
        <div>
          {this.state.loadingOnlinePayment ? (
            LayoutHelper.renderLoader()
          ) : (
            <Fragment>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  {this.renderStudioClassCard()}
                </Grid>
                <Grid item xs={12}>
                  {this.renderForm()}
                </Grid>
              </Grid>
              <Snackbar
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                open={this.state.showErrorSnackbar}
                onClose={() => {
                  this.setState({ showErrorSnackbar: false });
                }}
              >
                <SnackbarContent
                  message={this.props.translate('manageRegistration.bookingError')}
                  action={[
                    <IconButton
                      key="close"
                      aria-label="Close"
                      color="inherit"
                      onClick={() => {
                        this.setState({ showErrorSnackbar: false });
                      }}
                    >
                      <CloseIcon />
                    </IconButton>,
                  ]}
                  style={{
                    backgroundColor: '#d32f2f',
                  }}
                />
              </Snackbar>
            </Fragment>
          )}
        </div>
      </div>
    );
  }

  renderForm = () => {
    if (!this.state.studio || !this.state.classInfo || !this.state.owner) {
      return LayoutHelper.renderLoader();
    }
    if (this.isClassSignUpValid()) {
      return (
        <BookingClassInfoForm
          studio={this.state.studio}
          course={this.state.course}
          spotsLeft={this.state.spotsLeft}
          classInfo={this.state.classInfo}
          domain={this.props.domain}
          user={this.props.user}
          owner={this.state.owner}
          clientAttendance={this.state.clientAttendance}
          onFailure={(err) => {
            this.setState({ showErrorSnackbar: true });
            this.loadClassInfo(this.state.studio!);
          }}
          onSuccess={(attendance) => {
            this.props.clientAttendance(this.state.studio!, attendance.groupId, attendance._id);
          }}
        />
      );
    } else {
      if (this.state.clientAttendance) {
        return this.renderClientAttendanceLink(this.state.clientAttendance);
      } else {
        return (
          <Paper className={this.props.classes.paper}>
            <SnackbarContent message={this.props.translate('manageRegistration.classInThePast')} />
          </Paper>
        );
      }
    }
  };

  renderClientAttendanceLink = (clientAttendance: ClientAttendance) => {
    return (
      <Paper className={this.props.classes.paper}>
        <SnackbarContent
          message={
            <Typography align="center">
              {this.props.translate('manageRegistration.alreadySignedUpToTheClass')}
              <br />
              <Link
                href={routes.studioClientClasses({
                  ownerId: clientAttendance.ownerId,
                  studioClientId: clientAttendance.studioClientId,
                })}
              >
                {this.props.translate('common.myClasses')}
              </Link>
            </Typography>
          }
        />
      </Paper>
    );
  };

  isClassSignUpValid = (): boolean => {
    if (!this.state.classInfo) return false;

    if (this.state.classInfo.canceled) {
      return false;
    }
    if (this.state.clientAttendance) {
      return false;
    }
    if (this.state.spotsLeft === 0 || this.state.spotsLeft! < 0) {
      return false;
    }
    let classDateTime = moment.utc(this.state.classInfo.classDateTime);
    return LayoutHelper.isClassBookable(this.state.owner!.timezone, this.state.studio!, classDateTime);
  };

  renderStudioClassCard = () => {
    const termsAndConditions = UserHelper.getTermsAndConditions(this.state.owner);
    if (!this.state.studio || !this.state.owner || !this.state.classInfo) {
      return LayoutHelper.renderLoader();
    }
    let classDateTime = moment.utc(this.state.classInfo.classDateTime);
    return (
      <Card>
        <CardHeader
          action={
            <IconButton
              onClick={() => {
                this.setState({
                  showPricingDetails: !this.state.showPricingDetails,
                });
              }}
            >
              <InfoIcon />
            </IconButton>
          }
          avatar={<Avatar src={this.state.studio.avatar} className={this.props.classes.avatar}></Avatar>}
          title={
            <Fragment>
              {this.state.classInfo.className}&nbsp;
              {classDateTime.format('ddd DD MMM YYYY, HH:mm')}
            </Fragment>
          }
          subheader={<Link href={routes.getStudioLink(this.state.studio)}>{this.state.studio.name}</Link>}
        ></CardHeader>
        <Collapse in={this.state.showPricingDetails} timeout="auto">
          {this.renderClassInfo()}
          <CardContent>
            <div dangerouslySetInnerHTML={{ __html: this.state.studio.pricing }} className="text" />
          </CardContent>
          <CardContent>
            {termsAndConditions && (
              <Typography variant="caption">
                <Link href={termsAndConditions} target="_blank">
                  {this.props.translate('manageRegistration.termsAndConditions')}
                  &nbsp;{this.state.owner.name}
                </Link>
              </Typography>
            )}
          </CardContent>
        </Collapse>
      </Card>
    );
  };

  renderClassInfo = () => {
    if (!this.state.classInfo) {
      return LayoutHelper.renderLoader();
    }
    let classDateTime = moment.utc(this.state.classInfo.classDateTime);
    return (
      <CardContent>
        <List>
          <ListItem>
            <ListItemText
              primary={classDateTime.format('ddd DD MMM YYYY, HH:mm')}
              secondary={
                <Fragment>
                  {this.props.translate('manageRegistration.hostTimeZoneNotification')}:{' '}
                  {this.state.owner && moment.tz(this.state.owner.timezone).format('Z z')}
                </Fragment>
              }
            />
          </ListItem>

          {this.renderTeacher()}
          {this.renderInstructorName()}
          {this.renderRoom()}
          {this.renderAttendancePrice()}
          {this.renderClassInfoNote()}
          {this.renderClassLimit()}
        </List>
      </CardContent>
    );
  };

  renderClassLimit() {
    let classInfo = this.state.classInfo;
    if (classInfo && classInfo.limit) {
      return (
        <ListItem>
          <ListItemText
            primary={
              <span className="note-text">
                {this.props.translate('studio.classAvailability', {
                  availableSpots: this.state.spotsLeft,
                })}
              </span>
            }
          />
        </ListItem>
      );
    }
    return null;
  }

  renderTeacher() {
    if (this.state.classInfo && this.state.classInfo.teacherId) {
      return this.state.classInfo.teacherId.map((item) => {
        let teacher = this.state.teachersMap[item];
        if (teacher) {
          return (
            <ListItem key={teacher._id}>
              {teacher.avatar && (
                <ListItemIcon>
                  <Avatar src={teacher.avatar} />
                </ListItemIcon>
              )}
              <ListItemText primary={teacher.name} />
            </ListItem>
          );
        }
        return null;
      });
    }
    return null;
  }

  renderInstructorName() {
    if (this.state.classInfo && this.state.classInfo.instructorName) {
      return (
        <ListItem>
          <ListItemText primary={this.state.classInfo.instructorName} />
        </ListItem>
      );
    }
    return null;
  }

  renderRoom() {
    let roomNames = this.state.classInfo && this.state.classInfo.roomName;
    console.log('HERE');
    if (roomNames) {
      return roomNames.map((roomName, index) => {
        return (
          <ListItem key={index}>
            <ListItemText secondary={this.props.translate('studio.room')} primary={roomName} />
          </ListItem>
        );
      });
    }
    return null;
  }

  renderAttendancePrice() {
    if (this.state.classInfo && this.state.classInfo.attendancePrice) {
      return (
        <ListItem>
          <ListItemText
            secondary={this.props.translate('manageRegistration.attendancePrice')}
            primary={
              <Fragment>
                {this.state.classInfo.attendancePrice}
                {this.props.domain.currencyCode}
              </Fragment>
            }
          />
        </ListItem>
      );
    }
    return null;
  }

  renderClassInfoNote() {
    if (this.state.classInfo && this.state.classInfo.note) {
      return (
        <ListItem>
          <ListItemText
            secondary={this.props.translate('myClasses.note')}
            primary={<i dangerouslySetInnerHTML={{ __html: this.state.classInfo.note }} />}
          />
        </ListItem>
      );
    }
    return null;
  }
}

const mapStateToProps = (state: any, ownProps: any) => {
  const orderId = queryString.parse(ownProps.location.search).orderId as string;
  return {
    translate: getTranslate(state.locale),
    currentLanguage: getActiveLanguage(state.locale).code,
    user: state.user,
    studio: state.studio,
    domain: state.domain.domain,
    orderId: orderId,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    registerToClass: (studio: Studio, classId: string) => {
      if (studio.domainName) {
        dispatch(push(`${routes.class()}?keyword=${keyword}&location=${location}`));
      } else {
        dispatch(push(`${routes.classId()}?keyword=${keyword}`));
      }
    },
    toClassSignUp: (link: string) => {
      dispatch(push(link));
    },
    clientAttendance: (studio: Studio, groupId: string, attendanceId: string) => {
      dispatch(
        push(
          `${routes.clientAttendance({
            domainName: studio.domainName,
            groupId: groupId,
            id: attendanceId,
          })}`
        )
      );
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  withStyles((theme) => ({
    typography: {
      padding: theme.spacing(2),
    },
    card: {
      display: 'flex',
    },
    media: {
      height: 0,
      paddingTop: '56.25%', // 16:9
    },
    cover: {
      width: 150,
    },
    paper: {
      padding: theme.spacing(2),
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    root: {
      maxWidth: '400px',
      margin: 'auto',
      padding: theme.spacing(1),
    },
    schedule: {
      maxWidth: theme.spacing(170),
      margin: `0 auto`,
      padding: `0 ${theme.spacing(2)}px`,
    },
    rooms: {
      margin: theme.spacing(1),
      padding: `${theme.spacing(2)}px 0`,
    },
    formControl: {
      margin: theme.spacing(1),
      minWidth: theme.spacing(15),
    },
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
    coursesGrid: {
      paddingTop: theme.spacing(2),
    },
    expand: {
      transform: 'rotate(0deg)',
      marginLeft: 'auto',
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
      }),
    },
    expandOpen: {
      transform: 'rotate(180deg)',
    },
    buttons: {
      '& > *': {
        flex: 1,
      },
    },
    details: {
      display: 'flex',
      flexDirection: 'column',
    },
    studioCard: {},
    studioCover: {
      height: 0,
      paddingTop: '56.25%', // 16:9
    },
    bigAvatar: {
      width: 150,
      height: 150,
    },
  }))(BookingClassInfoView)
);
